import type { ReactNode, SyntheticEvent } from "react"

import { AnimatedNumber } from "@/components/animated-number"
import { Reveal } from "@/components/reveal"
import { Link } from "react-router-dom"

import { BrutalistBustHeroView } from "@/domains/landing-page/components/brutalist-bust-hero-view"
import { DISCORD_INVITE_LINK, DOCS_LINK } from "@/components/footer"
import {
  LANDING_STAT_LOADING,
  type LandingActivityItem,
  type LandingCapability,
  type LandingCapabilityIcon,
  type LandingCta,
  type LandingEpochCountdown,
  type LandingMetric,
  type LandingOpportunity,
  type LandingOpportunityRail,
  type LandingTokenLogo,
  type LandingPersonaIcon,
  type LandingPersonaRoute,
  type LandingTestimonial,
  type LandingTrustSignal,
  type ProfessionalLandingPageViewProps,
} from "@/domains/landing-page/interfaces"
import { cn } from "@/domains/library"

/** Shimmering placeholder shown in place of a stat value until live data arrives. */
const LandingStatSkeleton = ({ className }: { readonly className?: string }) => (
  <span className={cn("landing-skeleton inline-block h-[1em] w-24 align-middle", className)} aria-hidden />
)

/** Compact "Xs / Xm / Xh / Xd ago" label from a unix-seconds timestamp. */
const formatRelativeTime = (timestampSec: number): string => {
  const deltaSec = Math.max(0, Math.floor(Date.now() / 1000 - timestampSec))
  if (deltaSec < 60) return `${deltaSec}s ago`
  const deltaMin = Math.floor(deltaSec / 60)
  if (deltaMin < 60) return `${deltaMin}m ago`
  const deltaHr = Math.floor(deltaMin / 60)
  if (deltaHr < 24) return `${deltaHr}h ago`
  return `${Math.floor(deltaHr / 24)}d ago`
}

/**
 * Full-bleed marquee of recent on-chain swaps (real indexer data). The row is
 * duplicated so the CSS translate loop is seamless; hidden entirely when there
 * is no activity to show. Decorative for screen readers (the data also lives in
 * the app), so the track is marked aria-hidden.
 */
export const ActivityTickerView = ({ events }: { readonly events: readonly LandingActivityItem[] }) => {
  if (events.length === 0) return null

  const loop = [...events, ...events]

  return (
    <section className="relative z-10 border-y border-p400-light bg-p400/40 py-3">
      <div className="activity-marquee group flex overflow-hidden" aria-hidden>
        <div className="activity-marquee-track flex shrink-0 items-center gap-8 pr-8">
          {loop.map((event, index) => (
            <a
              key={`${event.id}-${index}`}
              href={event.txUrl}
              target="_blank"
              rel="noreferrer"
              className="flex shrink-0 items-center gap-2 text-xs font-medium text-t300 transition-colors hover:text-t100"
            >
              <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-success-text" />
              <span className="font-semibold uppercase tracking-[0.16em] text-primary-300">{event.kindLabel}</span>
              <span className="font-bold tabular-nums text-t100">{event.valueLabel}</span>
              {event.accountLabel ? <span className="font-mono text-t300">{event.accountLabel}</span> : null}
              <span className="text-t300">·</span>
              <span className="tabular-nums text-t300">{formatRelativeTime(event.timestampSec)}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

export const ProfessionalLandingPageView = ({
  brutalistHero,
  personaRoutes,
  epochCountdown,
  metrics,
  trustSignals,
  opportunityRails,
  capabilities,
  testimonials,
  heroCtas,
  heroAccount,
  activityEvents = [],
}: ProfessionalLandingPageViewProps) => {
  return (
    <main className="relative z-10 overflow-hidden bg-transparent text-t100">
      <BrutalistBustHeroView content={brutalistHero} account={heroAccount} />

      <ActivityTickerView events={activityEvents} />

      <LiveProofBandView
        personaRoutes={personaRoutes}
        epochCountdown={epochCountdown}
        metrics={metrics}
        trustSignals={trustSignals}
      />

      <FortyTwoSectionView />

      <LandingSectionView
        eyebrow="Top Pools"
        title="The pools paying the most this epoch."
        description="Browse by reward size or APR — scroll sideways to compare live opportunities."
      >
        <Reveal>
          <div className="space-y-10">
            {opportunityRails.map((rail, railIndex) => (
              <OpportunityRailView key={rail.title} rail={rail} railIndex={railIndex} />
            ))}
          </div>
        </Reveal>
      </LandingSectionView>

      <LandingSectionView
        eyebrow="How Scale Works"
        title="Swaps, locks, and emissions in one loop."
        description="Swaps, locks, incentives, and emissions all flow to veSCALE holders. Protocol volume becomes voting rewards each epoch."
      >
        <Reveal>
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
            {capabilities.map((capability) => (
              <ProductCapabilityCardView key={capability.title} capability={capability} />
            ))}
          </div>
        </Reveal>
      </LandingSectionView>

      <LandingSectionView
        eyebrow="Community Perspectives"
        title="What the community is building toward."
        description="Liquidity providers, partners, and governance voters on why they keep showing up for Scale."
      >
        <CommunityPerspectivesGridView testimonials={testimonials} />
      </LandingSectionView>

      <CommunitySocialProofSectionView />

      <section className="px-4 pb-24 pt-6 sm:px-6 lg:px-8">
        <Reveal className="relative mx-auto block max-w-7xl overflow-hidden rounded-[2rem] border border-primary-400/40 bg-gradient-to-br from-primary-700 via-primary-600 to-primary p-8 text-center shadow-2xl shadow-primary-700/30 md:p-16">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.18]"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgb(255 255 255 / 0.4) 1px, transparent 1px), linear-gradient(to bottom, rgb(255 255 255 / 0.4) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage: "radial-gradient(ellipse 70% 90% at 50% 0%, black 20%, transparent 80%)",
              WebkitMaskImage: "radial-gradient(ellipse 70% 90% at 50% 0%, black 20%, transparent 80%)",
            }}
            aria-hidden
          />
          <div className="relative z-10 mx-auto max-w-2xl">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-primary-100">Start Now</p>
            <h2 className="text-3xl font-bold tracking-tight text-t100 md:text-5xl">
              Your first swap is 30 seconds away.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-primary-100 md:text-lg">
              Explore live pools and APRs with no wallet required. Connect only when you&rsquo;re ready to earn.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              {heroCtas.map((cta) => (
                <a
                  key={`${cta.href}-${cta.label}`}
                  href={cta.href}
                  className={cn(
                    "inline-flex min-h-11 items-center justify-center rounded-xl px-6 py-3 text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    cta.tone === "primary"
                      ? "bg-white text-primary-700 shadow-lg shadow-black/20 hover:bg-primary-50"
                      : "border border-white/40 text-t100 hover:bg-white/10",
                  )}
                >
                  {cta.label}
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  )
}

export const LiveProofBandView = ({
  personaRoutes,
  epochCountdown,
  metrics,
  trustSignals,
}: {
  readonly personaRoutes: readonly LandingPersonaRoute[]
  readonly epochCountdown: LandingEpochCountdown
  readonly metrics: readonly LandingMetric[]
  readonly trustSignals: readonly LandingTrustSignal[]
}) => {
  return (
    <section className="relative px-4 pb-10 pt-12 sm:px-6 md:pt-16 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <Reveal>
          <MetricStripView metrics={metrics} />
        </Reveal>
        <div className="grid gap-6 lg:grid-cols-[0.92fr_1.08fr] lg:items-stretch">
          <Reveal delayMs={120}>
            <HeroEpochCountdownCardView countdown={epochCountdown} />
          </Reveal>
          <div className="flex flex-col gap-4">
            <Reveal delayMs={200} className="flex-1">
              <PersonaRoutingStripView routes={personaRoutes} />
            </Reveal>
            <Reveal delayMs={280}>
              <TrustStripView signals={trustSignals} />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  )
}

export const LandingSectionView = ({
  eyebrow,
  title,
  description,
  children,
  className,
}: {
  readonly eyebrow: string
  readonly title: string
  readonly description: string
  readonly children: ReactNode
  readonly className?: string
}) => {
  return (
    <section className={cn("px-4 py-16 sm:px-6 md:py-24 lg:px-8", className)}>
      <div className="mx-auto max-w-7xl">
        <Reveal className="mb-10 max-w-3xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-primary-300">{eyebrow}</p>
          <h2 className="text-3xl font-bold tracking-tight text-t100 md:text-5xl">{title}</h2>
          <p className="mt-4 text-base leading-7 text-t200 md:text-lg">{description}</p>
        </Reveal>
        {children}
      </div>
    </section>
  )
}

export const PersonaRoutingStripView = ({ routes }: { readonly routes: readonly LandingPersonaRoute[] }) => {
  return (
    <div className="grid h-full gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {routes.map((route) => (
        <a
          key={route.label}
          href={route.href}
          className="card-shine group flex h-full flex-col rounded-2xl border border-p400-light bg-p400/60 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-p300-light hover:bg-p400 hover:shadow-xl hover:shadow-primary-700/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-p300-light bg-p300 text-primary-300 transition-colors duration-200 group-hover:border-primary-400/50 group-hover:text-primary-200">
            <PersonaIcon icon={route.icon} />
          </span>
          <p className="text-sm font-bold text-t100 group-hover:text-primary-300">{route.label}</p>
          <p className="mt-1 text-xs leading-5 text-t300">{route.description}</p>
          <span className="mt-auto flex items-center gap-1.5 pt-5 text-xs font-semibold text-t300 transition-colors duration-200 group-hover:text-primary-300">
            Open
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className="transition-transform duration-200 group-hover:translate-x-0.5"
            >
              <path d="M5 12h14" />
              <path d="m13 6 6 6-6 6" />
            </svg>
          </span>
        </a>
      ))}
    </div>
  )
}

export const TrustStripView = ({ signals }: { readonly signals: readonly LandingTrustSignal[] }) => {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2 rounded-2xl border border-p400-light bg-p400/50 px-4 py-3 text-center text-sm">
      {signals.map((signal, index) => (
        <span key={signal.label} className="inline-flex items-center gap-2">
          {index > 0 ? <span className="text-t300" aria-hidden>·</span> : null}
          {signal.href ? (
            <a
              href={signal.href}
              className="font-semibold text-t200 transition-colors hover:text-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {signal.label}
            </a>
          ) : (
            <span className="font-semibold text-t200">{signal.label}</span>
          )}
        </span>
      ))}
    </div>
  )
}

const PersonaIcon = ({ icon }: { readonly icon: LandingPersonaIcon }) => {
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  }
  if (icon === "swap") {
    return (
      <svg {...common}>
        <path d="M4 8h13" />
        <path d="m14 5 3 3-3 3" />
        <path d="M20 16H7" />
        <path d="m10 19-3-3 3-3" />
      </svg>
    )
  }
  if (icon === "liquidity") {
    return (
      <svg {...common}>
        <path d="M12 2v4" />
        <path d="M12 18v4" />
        <path d="M4.93 4.93l2.83 2.83" />
        <path d="M16.24 16.24l2.83 2.83" />
        <path d="M2 12h4" />
        <path d="M18 12h4" />
        <path d="M4.93 19.07l2.83-2.83" />
        <path d="M16.24 7.76l2.83-2.83" />
      </svg>
    )
  }
  if (icon === "lock") {
    return (
      <svg {...common}>
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}

export const LandingCtaRowView = ({ ctas }: { readonly ctas: readonly LandingCta[] }) => {
  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      {ctas.map((cta) => (
        <a
          key={`${cta.href}-${cta.label}`}
          href={cta.href}
          className={cn(
            "inline-flex min-h-11 items-center justify-center rounded-xl border px-5 py-3 text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            cta.tone === "primary"
              ? "cta-glow border-primary-400 bg-primary text-t100 hover:bg-primary-light hover:shadow-lg hover:shadow-primary-700/25"
              : "border-p300-light bg-p300 text-t100 hover:bg-p300-light",
          )}
        >
          {cta.label}
        </a>
      ))}
    </div>
  )
}

export const HeroEpochCountdownCardView = ({ countdown }: { readonly countdown: LandingEpochCountdown }) => {
  return (
    <div className="rounded-[2rem] border border-p400-light bg-p400 p-4 shadow-2xl shadow-black/30">
      <div className="rounded-[1.5rem] border border-p300-light bg-p300 p-5">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-t100">Epoch {countdown.epochNumber}</h2>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-success-border/40 bg-success-background/15 px-3 py-1 text-xs font-semibold text-success-text">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-text opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-success-text" />
            </span>
            Live
          </span>
        </div>

        <div className="rounded-2xl border border-p400-light bg-p400 p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-t300">Epoch ends in</p>
          <p className="mt-2 font-mono text-3xl font-bold tabular-nums tracking-tight text-t100 md:text-4xl">
            {countdown.countdownLabel}
          </p>
        </div>

        <div className="mt-5">
          <CountdownProgressBarView
            label="This epoch"
            detail="Weekly reset"
            percent={countdown.weekProgressPercent}
          />
        </div>

        <div className="mt-5 space-y-3">
          <a
            href={countdown.voteHref}
            className="flex items-center justify-between gap-4 rounded-2xl border border-p400-light bg-p400 p-4 transition-all duration-200 hover:border-p300-light hover:bg-p400-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-t300">Epoch incentives</p>
              <p className="mt-1 text-sm text-t200">Paid out this epoch on-chain</p>
            </div>
            <p className="shrink-0 text-xl font-bold tabular-nums text-t100">
              {countdown.epochIncentivesValue === LANDING_STAT_LOADING ? (
                <LandingStatSkeleton className="w-20" />
              ) : (
                <AnimatedNumber value={countdown.epochIncentivesValue} delayMs={280} />
              )}
            </p>
          </a>
          <a
            href={countdown.voteHref}
            className="flex items-center justify-between gap-4 rounded-2xl border border-p400-light bg-p400 p-4 transition-all duration-200 hover:border-p300-light hover:bg-p400-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-t300">Bribe ROI</p>
              <p className="mt-1 text-sm text-t200">Returns you can verify on-chain</p>
            </div>
            <p className="shrink-0 text-xl font-bold tabular-nums text-success-text">
              {countdown.bribeRoiValue === LANDING_STAT_LOADING ? (
                <LandingStatSkeleton className="w-16" />
              ) : (
                <AnimatedNumber value={countdown.bribeRoiValue} delayMs={420} />
              )}
            </p>
          </a>
        </div>
      </div>
    </div>
  )
}

const CountdownProgressBarView = ({
  label,
  detail,
  percent,
}: {
  readonly label: string
  readonly detail: string
  readonly percent: number
}) => {
  const clampedPercent = Math.max(0, Math.min(100, percent))

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold text-t100">{label}</p>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-t300">{detail}</p>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-p400">
        <div
          className="h-full rounded-full bg-primary-400 transition-[width] duration-1000 ease-out"
          style={{ width: `${clampedPercent}%` }}
          role="progressbar"
          aria-valuenow={clampedPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label}
        />
      </div>
    </div>
  )
}

export const OpportunityRailView = ({
  rail,
  railIndex,
}: {
  readonly rail: LandingOpportunityRail
  readonly railIndex: number
}) => {
  return (
    <div>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-300">{rail.subtitle}</p>
          <h3 className="mt-1 text-xl font-bold tracking-tight text-t100 md:text-2xl">{rail.title}</h3>
        </div>
        {rail.href ? (
          <a
            href={rail.href}
            className="shrink-0 text-sm font-semibold text-t200 transition-colors hover:text-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            See all
          </a>
        ) : null}
      </div>
      <div className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2 [scrollbar-width:thin] lg:mx-0 lg:grid lg:grid-cols-4 lg:overflow-visible lg:px-0 lg:pb-0">
        {rail.opportunities.map((opportunity, index) => (
          <ProductOpportunityCardView
            key={`${rail.title}-${opportunity.href}`}
            opportunity={opportunity}
            delayMs={200 + railIndex * 80 + index * 120}
            className="w-[min(100%,17.5rem)] shrink-0 snap-start lg:w-full lg:shrink"
          />
        ))}
      </div>
    </div>
  )
}

export const MetricStripView = ({ metrics }: { readonly metrics: readonly LandingMetric[] }) => {
  return (
    <div className="grid gap-3 rounded-[2rem] border border-p400-light bg-p400/80 p-3 backdrop-blur md:grid-cols-4">
      {metrics.map((metric, index) => (
        <MetricCardView key={metric.label} metric={metric} delayMs={index * 120} />
      ))}
    </div>
  )
}

export const MetricCardView = ({ metric, delayMs = 0 }: { readonly metric: LandingMetric; readonly delayMs?: number }) => {
  const isLoading = metric.value === LANDING_STAT_LOADING
  const content = (
    <>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-t300">{metric.label}</p>
      <p className="mt-3 text-3xl font-bold tracking-tight tabular-nums text-t100">
        {isLoading ? <LandingStatSkeleton className="w-28" /> : <AnimatedNumber value={metric.value} delayMs={delayMs} />}
      </p>
      {metric.detail ? <p className="mt-2 text-sm text-t200">{metric.detail}</p> : null}
    </>
  )

  const className =
    "card-shine rounded-2xl border border-p300-light bg-p300 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-p300-light hover:bg-p300-light"

  if (metric.href) {
    return (
      <a href={metric.href} className={cn(className, "block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}>
        {content}
      </a>
    )
  }

  return <div className={className}>{content}</div>
}

const OpportunityCardTokenLogoView = ({ token }: { readonly token: LandingTokenLogo }) => {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-p400">
      <img
        src={token.imageUrl}
        alt={token.symbol}
        className="h-full w-full object-cover"
        onError={(event: SyntheticEvent<HTMLImageElement>) => {
          event.currentTarget.onerror = null
          event.currentTarget.src = `/assets/${token.chainSlug}/unknown-logo.png`
        }}
      />
    </div>
  )
}

export const ProductOpportunityCardView = ({
  opportunity,
  delayMs = 0,
  className,
}: {
  readonly opportunity: LandingOpportunity
  readonly delayMs?: number
  readonly className?: string
}) => {
  const tokenLogos = opportunity.tokenLogos ?? []

  return (
    <a
      href={opportunity.href}
      className={cn(
        "card-shine group block h-full min-w-0 rounded-2xl border border-p400-light bg-p400 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-p300-light hover:bg-p400-light hover:shadow-xl hover:shadow-primary-700/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {tokenLogos.length > 0 ? (
          <div className="flex -space-x-3">
            {tokenLogos.map((token) => (
              <OpportunityCardTokenLogoView key={token.symbol} token={token} />
            ))}
          </div>
        ) : null}
        <span className="ml-auto shrink-0 rounded border border-primary-700/20 bg-primary-700/10 px-2 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary-300">
          {opportunity.subtitle}
        </span>
      </div>
      <h3 className="mt-4 text-lg font-bold text-t100 group-hover:text-primary-300">{opportunity.title}</h3>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-t300">Rewards</p>
          <p className="mt-1 text-lg font-bold tabular-nums text-t100">
            <AnimatedNumber value={opportunity.primaryValue} delayMs={delayMs} />
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-t300">APR</p>
          <p className="mt-1 text-lg font-bold tabular-nums text-success-text">
            <AnimatedNumber value={opportunity.secondaryValue} delayMs={delayMs + 120} />
          </p>
        </div>
      </div>
    </a>
  )
}

export const ProductCapabilityCardView = ({ capability }: { readonly capability: LandingCapability }) => {
  return (
    <div className="card-shine group h-full rounded-2xl border border-p400-light bg-p400 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-p300-light hover:bg-p400-light">
      <div className="mb-5 flex items-center gap-3">
        {capability.icon ? (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-p300-light bg-p300 text-primary-300 transition-colors duration-300 group-hover:border-primary-400/50 group-hover:text-primary-200">
            <CapabilityIcon icon={capability.icon} />
          </span>
        ) : null}
        {capability.stat ? (
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-primary-300">{capability.stat}</p>
        ) : null}
      </div>
      <h3 className="text-xl font-bold text-t100">{capability.title}</h3>
      <p className="mt-3 text-sm leading-6 text-t200">{capability.description}</p>
    </div>
  )
}

const CapabilityIcon = ({ icon }: { readonly icon: LandingCapabilityIcon }) => {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  }
  if (icon === "swap") {
    return (
      <svg {...common}>
        <path d="M4 8h13" />
        <path d="m14 5 3 3-3 3" />
        <path d="M20 16H7" />
        <path d="m10 19-3-3 3-3" />
      </svg>
    )
  }
  if (icon === "lock") {
    return (
      <svg {...common}>
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        <path d="M12 15v2" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="m7 15 3-4 3 3 4-6" />
    </svg>
  )
}

export const FortyTwoSectionView = () => {
  return (
    <section className="px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <div className="grid items-center gap-8 rounded-[2rem] border border-p400-light bg-p400 p-6 shadow-2xl shadow-black/20 md:p-10 lg:grid-cols-[1fr_1.1fr]">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-primary-300">
                Guaranteed Emissions
              </p>
              <h2 className="text-3xl font-bold tracking-tight text-t100 md:text-5xl">Forty-two farming days.</h2>
              <p className="mt-4 max-w-xl text-base leading-7 text-t200 md:text-lg">
                Scale is the only ve(3,3) that guarantees farming rewards for a minimum of 42 days. Emissions can only
                hold or rise over that window, so there are no &ldquo;zero vote&rdquo; weeks and your returns stay
                predictable.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <FortyTwoTag>No zero-emission weeks</FortyTwoTag>
                <FortyTwoTag>Deeper sticky liquidity</FortyTwoTag>
                <FortyTwoTag>Predictable APR</FortyTwoTag>
              </div>
            </div>
            <div className="rounded-[1.5rem] border border-p300-light bg-p300 p-5 sm:p-6">
              <EmissionsCurveSvg />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

const FortyTwoTag = ({ children }: { readonly children: ReactNode }) => (
  <span className="inline-flex items-center rounded-full border border-p300-light bg-p300 px-3 py-1 text-xs font-semibold text-t200">
    {children}
  </span>
)

/**
 * Decorative emissions chart. Scale's emissions hold a guaranteed plateau across 42 days
 * while a typical ve(3,3) line collapses into zero-vote gaps. The lines draw themselves
 * once the parent Reveal scrolls into view (see .reveal--visible rules in globals.css).
 */
const EmissionsCurveSvg = () => {
  return (
    <div className="relative">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <p className="text-6xl font-bold leading-none tracking-tight text-t100 md:text-7xl">42</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.28em] text-t300">Days guaranteed</p>
        </div>
        <div className="space-y-1.5 text-right">
          <span className="flex items-center justify-end gap-2 text-xs font-semibold text-t200">
            <span className="h-1.5 w-4 rounded-full bg-primary-400" /> Scale emissions
          </span>
          <span className="flex items-center justify-end gap-2 text-xs font-semibold text-t300">
            <span className="h-1.5 w-4 rounded-full bg-t300/50" /> Typical ve(3,3)
          </span>
        </div>
      </div>
      <svg viewBox="0 0 320 150" className="h-auto w-full" role="img" aria-label="Scale emissions stay flat for 42 days while a typical ve(3,3) drops to zero">
        <defs>
          <linearGradient id="emissionsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--primary-400))" stopOpacity="0.28" />
            <stop offset="100%" stopColor="hsl(var(--primary-400))" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[30, 65, 100, 135].map((y) => (
          <line key={y} x1="0" y1={y} x2="320" y2={y} stroke="currentColor" strokeWidth="1" className="text-p400-light" />
        ))}
        <path
          d="M0 132 C 40 120, 70 112, 110 60 C 150 22, 190 60, 230 110 C 260 132, 290 138, 320 140"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeDasharray="4 5"
          className="emissions-path emissions-path--rival text-t300"
          pathLength={1}
        />
        <path
          d="M0 120 C 30 70, 70 40, 120 38 C 180 36, 240 42, 320 44"
          fill="url(#emissionsFill)"
          stroke="none"
          className="emissions-fill"
        />
        <path
          d="M0 120 C 30 70, 70 40, 120 38 C 180 36, 240 42, 320 44"
          fill="none"
          stroke="hsl(var(--primary-400))"
          strokeWidth="2.5"
          className="emissions-path emissions-path--scale"
          pathLength={1}
        />
        {[
          [40, 71],
          [120, 38],
          [200, 39],
          [288, 43],
        ].map(([cx, cy]) => (
          <circle key={cx} cx={cx} cy={cy} r="3.5" fill="hsl(var(--primary-400))" className="emissions-dot" />
        ))}
      </svg>
    </div>
  )
}

export const CommunityPerspectivesGridView = ({
  testimonials,
}: {
  readonly testimonials: readonly LandingTestimonial[]
}) => {
  return (
    <Reveal>
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {testimonials.map((testimonial) => (
          <TestimonialCardView key={testimonial.authorName} testimonial={testimonial} />
        ))}
      </div>
    </Reveal>
  )
}

const TestimonialCardView = ({ testimonial }: { readonly testimonial: LandingTestimonial }) => {
  return (
    <figure className="card-shine flex h-full flex-col rounded-2xl border border-p400-light bg-p400 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-p300-light hover:bg-p400-light">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" className="mb-4 text-primary-400/60" aria-hidden>
        <path d="M9.5 5C6.5 5 4 7.5 4 11v8h7v-8H7c0-2 1.5-3.5 3.5-3.5V5zm9 0c-3 0-5.5 2.5-5.5 6v8h7v-8h-4c0-2 1.5-3.5 3.5-3.5V5z" />
      </svg>
      <blockquote className="flex-grow space-y-3 text-sm leading-6 text-t200">
        {testimonial.quote.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </blockquote>
      <figcaption className="mt-5 flex items-center gap-3 border-t border-p300-light pt-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-700 text-xs font-bold text-t100">
          {testimonial.initials}
        </span>
        <span>
          <span className="block text-sm font-semibold text-t100">{testimonial.authorName}</span>
          <span className="block text-xs font-medium text-primary-300">{testimonial.role}</span>
        </span>
      </figcaption>
    </figure>
  )
}

export const CommunitySocialProofSectionView = () => {
  return (
    <section className="px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal className="overflow-hidden rounded-[2rem] border border-p400-light bg-p400 p-6 shadow-2xl shadow-black/20 md:p-10">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-primary-300">Community</p>
              <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-t100 md:text-5xl">
                Where the community shapes Scale
              </h2>
              <p className="mt-4 max-w-xl text-base leading-7 text-t200 md:text-lg">
                Our Discord is where it happens — daily conversation, shared alpha, and real debate. A small but active
                community is shaping the future direction of Scale. Jump in and add your voice.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={DISCORD_INVITE_LINK}
                  target="_blank"
                  rel="noreferrer nofollow"
                  className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-primary-400 bg-primary px-5 py-3 text-sm font-bold text-t100 transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-light hover:shadow-lg hover:shadow-primary-700/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <DiscordIcon className="h-4 w-4" />
                  Join Discord
                </a>
                <Link
                  to={DOCS_LINK}
                  className="inline-flex min-h-11 items-center justify-center rounded-xl border border-p300-light bg-p300 px-5 py-3 text-sm font-bold text-t100 transition-all duration-200 hover:-translate-y-0.5 hover:bg-p300-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  View Docs
                </Link>
                <Link
                  to="/blog"
                  className="inline-flex min-h-11 items-center justify-center rounded-xl border border-p300-light bg-p300 px-5 py-3 text-sm font-bold text-t100 transition-all duration-200 hover:-translate-y-0.5 hover:bg-p300-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  View Blog
                </Link>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <CommunityCodeBlockView />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

const CommunityCodeBlockView = () => {
  return (
    <div className="relative w-full max-w-sm rotate-2 rounded-2xl border border-p300-light bg-p300 p-6 shadow-2xl shadow-black/30 transition-transform duration-500 hover:rotate-0">
      <div className="font-mono text-xs leading-relaxed text-primary-300">
        <span className="text-primary-200">const</span> <span className="text-t100">ScaleV3</span> ={" "}
        <span className="text-primary-200">new</span> Protocol({"{"}
        <br />
        &nbsp;&nbsp;liquidity: <span className="text-success-text">&quot;Deep&quot;</span>,<br />
        &nbsp;&nbsp;yield: <span className="text-success-text">&quot;Real&quot;</span>,<br />
        &nbsp;&nbsp;community: <span className="text-success-text">&quot;Based&quot;</span>
        <br />
        {"}"});
        <br />
        <br />
        <span className="text-t300">// Deploying to Base...</span>
        <br />
        ScaleV3.<span className="text-primary-200">launch</span>();
      </div>
    </div>
  )
}

const DiscordIcon = ({ className }: { readonly className?: string }) => {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.176 2.419 0 1.334-.966 2.419-2.176 2.419zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.176 2.419 0 1.334-.966 2.419-2.176 2.419z" />
    </svg>
  )
}
